import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import SupplierPriceService from "services/SupplierPriceService";
import Utils from "utils";
import openNotification from "utils/notification";

export const getSupplierPricesApi = createAsyncThunk(
  "supplierPrice/getSupplierPricesApi",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SupplierPriceService.getSupplierPrices(data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const upsertSupplierPricesApi = createAsyncThunk(
  "supplierPrice/upsertSupplierPricesApi",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;
      const response = await SupplierPriceService.upsertSupplierPrices(payload);
      if (onSuccess) onSuccess(response);

      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title"),
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const addSupplierPricesApi = createAsyncThunk(
  "supplierPrice/addSupplierPricesApi",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data || {};
      const payload = cloneDeep(data);
      delete payload.onSuccess;

      const response = await SupplierPriceService.addSupplierPrices(payload);

      if (onSuccess) onSuccess(response);

      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title"),
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const getSupplierPriceHistoryApi = createAsyncThunk(
  "supplierPrice/getSupplierPriceHistoryApi",
  async (id, { rejectWithValue }) => {
    try {
      const response = await SupplierPriceService.getPriceHistory(id);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const getSupplierHistoryBySupplierApi = createAsyncThunk(
  "supplierPrice/getSupplierHistoryBySupplierApi",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SupplierPriceService.getHistoryBySupplier(data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const deleteSupplierPriceApi = createAsyncThunk(
  "supplierPrice/deleteSupplierPriceApi",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess, id } = data;

      const response = await SupplierPriceService.deleteSupplierPrice(id);

      if (onSuccess) onSuccess(response);

      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title"),
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const lookupSupplierPricesApi = createAsyncThunk(
  "supplierPrice/lookupSupplierPricesApi",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data || {};
      const payload = cloneDeep(data);
      delete payload.onSuccess;

      const response = await SupplierPriceService.lookupPrices(payload);

      if (onSuccess) onSuccess(response);

      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const getSuppliersApi = createAsyncThunk(
  "supplierPrice/getSuppliersApi",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SupplierPriceService.getSuppliers(data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const exportSuppliersExcelApi = createAsyncThunk(
  "supplierPrice/exportSuppliersExcelApi",
  async (data, { rejectWithValue }) => {
    try {
      const blob = await SupplierPriceService.exportSuppliers(data);
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = "Danh sách nhà cung cấp.xlsx";

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);

      return true;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

const unwrapList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

const initialState = {
  loading: false,
  addLoading: false,
  loadingExport: false,

  supplierPriceList: [],
  supplierPriceHistory: [],
  supplierHistoryList: [],
  supplierHistoryLoading: false,
  supplierPriceLookup: [],

  totalCount: 0,

  currentRequestId: undefined,

  supplierList: [],
  supplierTotalCount: 0,
};

export const supplierPriceSlice = createSlice({
  name: "supplierPrice",
  initialState,
  reducers: {
    showLoading: (state) => {
      state.loading = true;
    },
    setSupplierPriceHistory: (state, action) => {
      state.supplierPriceHistory = action.payload || [];
    },
    setSupplierHistoryList: (state, action) => {
      state.supplierHistoryList = action.payload || [];
    },
    setSupplierPriceLookup: (state, action) => {
      state.supplierPriceLookup = action.payload || [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSupplierPricesApi.pending, (state, action) => {
        state.loading = true;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(getSupplierPricesApi.fulfilled, (state, action) => {
        state.loading = false;

        const list = unwrapList(action.payload);
        state.supplierPriceList = list;
        state.totalCount = action.payload?.data?.totalCount || list.length;
      })
      .addCase(getSupplierPricesApi.rejected, (state) => {
        state.loading = false;
      })

      .addCase(upsertSupplierPricesApi.pending, (state) => {
        state.loading = true;
      })
      .addCase(upsertSupplierPricesApi.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(upsertSupplierPricesApi.rejected, (state) => {
        state.loading = false;
      })

      .addCase(addSupplierPricesApi.pending, (state) => {
        state.addLoading = true;
      })
      .addCase(addSupplierPricesApi.fulfilled, (state) => {
        state.addLoading = false;
      })
      .addCase(addSupplierPricesApi.rejected, (state) => {
        state.addLoading = false;
      })

      .addCase(getSupplierPriceHistoryApi.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSupplierPriceHistoryApi.fulfilled, (state, action) => {
        state.loading = false;
        state.supplierPriceHistory = unwrapList(action.payload);
      })
      .addCase(getSupplierPriceHistoryApi.rejected, (state) => {
        state.loading = false;
      })

      .addCase(getSupplierHistoryBySupplierApi.pending, (state) => {
        state.supplierHistoryLoading = true;
      })
      .addCase(getSupplierHistoryBySupplierApi.fulfilled, (state, action) => {
        state.supplierHistoryLoading = false;
        state.supplierHistoryList = unwrapList(action.payload);
      })
      .addCase(getSupplierHistoryBySupplierApi.rejected, (state) => {
        state.supplierHistoryLoading = false;
      })

      .addCase(deleteSupplierPriceApi.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteSupplierPriceApi.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteSupplierPriceApi.rejected, (state) => {
        state.loading = false;
      })

      .addCase(lookupSupplierPricesApi.pending, (state) => {
        state.loading = true;
      })
      .addCase(lookupSupplierPricesApi.fulfilled, (state, action) => {
        state.loading = false;
        state.supplierPriceLookup = unwrapList(action.payload);
      })
      .addCase(lookupSupplierPricesApi.rejected, (state) => {
        state.loading = false;
      })

      .addCase(getSuppliersApi.pending, (state, action) => {
        state.loading = true;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(getSuppliersApi.fulfilled, (state, action) => {
        state.loading = false;

        const list = unwrapList(action.payload);
        state.supplierList = action.payload;
        state.supplierTotalCount = action.payload?.totalCount || list.length;
      })
      .addCase(getSuppliersApi.rejected, (state) => {
        state.loading = false;
      })

      .addCase(exportSuppliersExcelApi.pending, (state) => {
        state.loadingExport = true;
      })
      .addCase(exportSuppliersExcelApi.fulfilled, (state) => {
        state.loadingExport = false;
      })
      .addCase(exportSuppliersExcelApi.rejected, (state) => {
        state.loadingExport = false;
      });
  },
});

export const {
  showLoading,
  setSupplierPriceHistory,
  setSupplierHistoryList,
  setSupplierPriceLookup,
} = supplierPriceSlice.actions;

export default supplierPriceSlice.reducer;